import {JoinType} from '@/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/interface';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { FieldType } from '@dbl/core/dataSource';
import { ChartTypeInfos, SwitchChartTypes } from '@dbl/core/chartEditor';

export const getJoinRelationClazz = (connectType?: string) => {
  let joinArrow = '';
  let joinIcon = '';
  if (connectType === JoinType.RightJoin) {
    joinArrow = 'right-otherJoin';
    joinIcon = 'right-join';
  } else if (connectType === JoinType.LeftJoin) {
    joinArrow = 'left-otherJoin';
    joinIcon = 'left-join';
  } else if (connectType === JoinType.FullJoin) {
    joinArrow = 'all-otherJoin';
    joinIcon = 'all-join';
  } else if (connectType === JoinType.InnerJoin) {
    joinArrow = 'init-otherJoin';
    joinIcon = 'init-join';
  }
  return [joinArrow, joinIcon];
};

export const getChartTypeInfos = () => {
  const chartInfoMap: Record<TChartType, {cn: string; en: string; }> = {};
  
  SwitchChartTypes?.forEach(type => {
    const {metric, dimension, showDimension, xAxis, nameCn, nameEn , info } = ChartTypeInfos?.[type] || {};
    const isAdds = ![ChartType.MixedChart, ChartType.DimNumberTrend, ChartType.Map].includes(type) ? 's' : '';
    
    const infos = [];
    
    switch (type) {
      case ChartType.Trend: {
        infos.push(xAxis.info);
        break;
      }
      case ChartType.DimNumberTrend: {
        infos.push(xAxis.info, showDimension.info);
        break;
      }
      case ChartType.BigNumber: break;
      default: {
        infos.push(dimension.info);
      }
    }
    
    infos.push(metric.info);
    
    const titleCn = `对于${nameCn}，请尝试`;
    const titleEn = `For ${nameEn.toLowerCase()}${isAdds} try`;
    let cn = `${titleCn}<br/>`;
    let en = `${titleEn}<br/>`;
    
    infos.forEach(info => {
      cn += `${info.cn}<br/>`;
      en += `${info.en}<br/>`;
    });
  
    chartInfoMap[type] = info || {cn, en};
  });
  
  return chartInfoMap;
};

/**
 * 校验 过滤条件, 指标, 维度等是否符合绘制图表的权限
 * @param condition 校验条件
 * @param source  用于校验的数据
 * @param isEn  用于校验的数据
 * @return isCheckSuccess 返回true代表 校验条件通过, 可以计算; info是提示文本
 */
export function checkConditions(condition: any, source: any[], isEn: boolean): {isCheckSuccess: boolean; info: any; disabledError: string;} {
  if (!condition) return {isCheckSuccess: true, info: '', disabledError: ''}; // 说明不需要判断这个条件
  const {operator, count, haveDate, oneMoreHaveDate, info, disabled, hidden} = condition;
  let result = hidden; // 如果隐藏了某个筛选类别, 那么相当于直接校验通过
  if (!result) {
    switch (operator) {
      case '>=':
        result = source?.length >= count;
        break;
      case '<=':
        result = source?.length > 0 && source?.length <= count;
        break;
      case '===':
        result = source?.length === count;
    }
  
    if (haveDate && result) {
      result = source.some(item => item.column_type === FieldType.Date);
    }
    // 超过1个维度需要日期维度限制
    if (oneMoreHaveDate && result && source?.length > 1) {
      result = source.some(item => item.column_type === FieldType.Date);
    }
  }
  
  return {
    isCheckSuccess: result,
    info: info?.en && (isEn ? ` ${info.en}` : info.cn), // info.en主要是用来判断是否有文本, info.en和info.cn肯定是同时有值或者无值的
    disabledError: isEn ? disabled?.en : disabled?.cn,
  };
}

/**
 * 校验 过滤条件, 统计指标, 分类维度是否都符合条件
 * @param source 数据
 * @param chartType 图表类型
 * @param isEn  用于校验的数据
 */
export function checkAllConditions(chartType: string, source: { columnFilters: any[]; columnCompares: any[]; columnMetrics: any[]; columnDimensions: any[]; columnX: any[]; columnShowDimensions: any[];}, isEn: boolean) {
  try {
    
    const {
      columnFilters, columnCompares, columnMetrics, columnDimensions, columnX, columnShowDimensions
    } = source;
    
    const isCleared = columnFilters?.length === 0 && columnCompares?.length === 0 && columnMetrics?.length === 0 && columnDimensions?.length === 0 && columnShowDimensions?.length === 0;
    
    const {filter, compare, metric, dimension, showDimension, xAxis, nameCn, nameEn, info } = ChartTypeInfos[chartType];
    
    const filterResult = checkConditions(filter, columnFilters, isEn);
    const compareResult = checkConditions(compare, columnCompares, isEn);
    const metricResult = checkConditions(metric, columnMetrics, isEn);
    const dimensionResult = checkConditions(dimension, columnDimensions, isEn);
    const xResult = checkConditions(xAxis, columnX, isEn);
    const showDimensionResult = checkConditions(showDimension, columnShowDimensions, isEn);  // 展示维度
    const isAdds = ![ChartType.MixedChart, ChartType.DimNumberTrend, ChartType.Map].includes(chartType as TChartType) ? 's' : '';
    const title = isEn ? `For ${nameEn.toLowerCase()}${isAdds} try` : `对于${nameCn}，请尝试`;
    // 自定义info提示
    const infoContent = info ? `${info?.en && (isEn ? ` ${info?.en}` : info?.cn)}` : null;

    return {
      isCheckSuccess: filterResult.isCheckSuccess && compareResult.isCheckSuccess && metricResult.isCheckSuccess && dimensionResult.isCheckSuccess && xResult.isCheckSuccess && (chartType === ChartType.DimNumberTrend ? showDimensionResult.isCheckSuccess : true),
      isCleared,
      bubbleContent: infoContent || `${title}<br/>
      ${dimensionResult.info ? `${dimensionResult.info}<br/>` : ''}
      ${xResult.info ? `${xResult.info}<br/>` : ''}
      ${showDimensionResult.info ? `${showDimensionResult.info}<br/>` : ''}
      ${metricResult.info}
    `
    };
    
    
  } catch (e) {
    
    console.error("checkAllConditions error: ", e);
    return {
      isCheckSuccess: true,
      isCleared: false,
      bubbleContent: '',
    };
  }
  
}

// 计算得到原始数据的状态下, 筛选区域的条件限制判断
export function checkRawColumnModeConditions(chartType: string, source: {columnFilters: any[]; columnRawColumns: any[];}, isEn: boolean) {
  
  const {
    columnFilters, columnRawColumns
  } = source;
  
  const isCleared = columnFilters?.length === 0 && columnRawColumns?.length === 0;
  
  const { rowColumn, nameCn, nameEn} = ChartTypeInfos[chartType];
  
  // const filterResult = checkConditions({...filter, count: 0}, columnFilters, isEn);
  // const rowColumnResult = checkConditions(rowColumn, columnRawColumns, isEn);
  const isAdds = ![ChartType.MixedChart].includes(chartType as TChartType) ? 's' : '';
  
  const title = isEn ? `For ${nameEn.toLowerCase()}${isAdds} try` : `对于${nameCn}，请尝试`;
  
  return {
    isCheckSuccess: columnRawColumns?.length > 0,
    isCleared,
    bubbleContent: `${title}<br/>
      ${isEn ? rowColumn?.info?.en : rowColumn?.info?.cn}
    `
  };
}
